import { computed, ref } from "vue";
import type { BlockedUser, BlockedUsersResponse } from "../../shared/api.js";
import { apiGet, apiPost, apiDelete } from "../utils/api.js";

/**
 * The signed-in user's block list, shared across pages.
 *
 * The server already hides forum content authored by blocked people; chat is
 * delivered over one broadcast socket, so the client filters those messages
 * with the same list. A block is matched on the registered account id when the
 * blocked person has one, and otherwise on the display name, which is all a
 * pure guest ever exposes.
 */
const blocks = ref<BlockedUser[]>([]);
const loaded = ref(false);
let inFlight: Promise<void> | null = null;

const blockedUserIds = computed(
  () => new Set(blocks.value.map((b) => b.blockedUserId).filter((id): id is string => !!id)),
);
const blockedNames = computed(() => new Set(blocks.value.map((b) => b.blockedDisplayName)));

function apply(response: BlockedUsersResponse): void {
  blocks.value = response.blocks;
  loaded.value = true;
}

export function useBlocks() {
  /** Load once per session; safe to call from several components at once. */
  async function loadBlocks(force = false): Promise<void> {
    if (loaded.value && !force) return;
    if (inFlight) return inFlight;
    inFlight = (async () => {
      try {
        apply(await apiGet<BlockedUsersResponse>("/api/blocks"));
      } catch {
        // Signed-out visitors get a 401 here and simply have no blocks.
        blocks.value = [];
        loaded.value = true;
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  }

  async function blockUser(target: { userId?: string | null; displayName: string }): Promise<void> {
    apply(
      await apiPost<BlockedUsersResponse>(
        "/api/blocks",
        { blockedUserId: target.userId ?? null, blockedDisplayName: target.displayName },
        "Failed to block user",
      ),
    );
  }

  async function unblockUser(blockId: string): Promise<void> {
    apply(await apiDelete<BlockedUsersResponse>(`/api/blocks/${blockId}`, "Failed to unblock user"));
  }

  /** True when content from this author should be hidden from the viewer. */
  function isBlocked(target: { userId?: string | null; displayName?: string | null }): boolean {
    if (target.userId && blockedUserIds.value.has(target.userId)) return true;
    return !!target.displayName && blockedNames.value.has(target.displayName);
  }

  function clearBlocks(): void {
    blocks.value = [];
    loaded.value = false;
  }

  return { blocks, loadBlocks, blockUser, unblockUser, isBlocked, clearBlocks };
}
